import { getSupShopTagListByTagType } from '@/service/apis/kl'

export const RESERVATION_TYPE = {
  QUANTITY: 1,
  GMV: 2
}

export const COMMISSION_TYPE = {
  RATE: 1,
  FIXED: 2
}

export const VOLUME_STATUS = {
  1: '待沟通',
  2: '沟通中',
  3: '已完成'
}

export const COMMISSION_TYPE_OPTIONS = [
  { label: '佣金比例', value: COMMISSION_TYPE.RATE },
  { label: '固定金额', value: COMMISSION_TYPE.FIXED }
]

export const VOLUME_STATUS_OPTIONS = [
  { label: '待沟通', value: 1 },
  { label: '沟通中', value: 2 },
  { label: '已完成', value: 3 }
]

export function formatVolumeStatus(status) {
  return VOLUME_STATUS[status] || '--'
}

export function getVolumeStatusTagType(status) {
  const typeMap = {
    1: 'warning',
    2: 'info',
    3: 'success'
  }
  return typeMap[status] || 'info'
}

export function getProductFullName(product = {}) {
  return [product.categoryName, product.brandName, product.productName]
    .filter(item => item)
    .join(' ')
}

export function formatCommissionText(commissionType, promotionRate, promotionAmount) {
  if (!commissionType) return '--'
  if (commissionType === COMMISSION_TYPE.RATE) {
    return `佣金比例${promotionRate != null ? promotionRate : '--'}%`
  }
  return `固定金额${promotionAmount != null ? promotionAmount : '--'}元/件`
}

export function formatReservationQtyText(qty) {
  return qty != null && qty !== '' ? `${qty}件` : '--'
}

function formatReservationAmountText(reservationType, qty, gmv) {
  if (reservationType === RESERVATION_TYPE.GMV) {
    return gmv != null && gmv !== '' ? `${gmv}元` : '--'
  }
  return formatReservationQtyText(qty)
}

function hasAfterValue(value) {
  return value != null && value !== ''
}

function formatChangeLogPromotionAmount(value) {
  if (value == null || value === '') return '--'
  return `${value}元/件`
}

function formatChangeLogPromotionRate(value) {
  if (value == null || value === '') return '--'
  return `${value}%`
}

function formatChangeLogCommissionValue(commissionType, promotionRate, promotionAmount) {
  if (commissionType === COMMISSION_TYPE.RATE) {
    return `佣金比例${formatChangeLogPromotionRate(promotionRate)}`
  }
  if (commissionType === COMMISSION_TYPE.FIXED) {
    return `固定金额${formatChangeLogPromotionAmount(promotionAmount)}`
  }
  if (promotionRate != null && promotionRate !== '') {
    return `佣金比例${formatChangeLogPromotionRate(promotionRate)}`
  }
  if (promotionAmount != null && promotionAmount !== '') {
    return `固定金额${formatChangeLogPromotionAmount(promotionAmount)}`
  }
  return '--'
}

function hasCommissionChange(row = {}) {
  return hasAfterValue(row.afterCommissionType)
    || hasAfterValue(row.afterPromotionRate)
    || hasAfterValue(row.afterPromotionAmount)
}

export function formatChangeLogItem(row = {}) {
  const reservationBefore = formatReservationAmountText(
    row.beforeReservationType,
    row.beforeReservationQty,
    row.beforeReservationGmv
  )
  const hasReservationChange = hasAfterValue(row.afterReservationQty)
    || hasAfterValue(row.afterReservationGmv)
    || hasAfterValue(row.afterReservationType)
  const reservationAfter = hasReservationChange
    ? formatReservationAmountText(
      row.afterReservationType || row.beforeReservationType,
      row.afterReservationQty,
      row.afterReservationGmv
    )
    : ''
  const commissionBefore = formatChangeLogCommissionValue(
    row.beforeCommissionType,
    row.beforePromotionRate,
    row.beforePromotionAmount
  )
  const commissionAfter = hasCommissionChange(row)
    ? formatChangeLogCommissionValue(
      row.afterCommissionType || row.beforeCommissionType,
      row.afterPromotionRate,
      row.afterPromotionAmount
    )
    : ''
  const expectedOrderTimeBefore = row.beforeExpectedOrderTime || '--'
  const expectedOrderTimeAfter = row.afterExpectedOrderTime || ''

  return {
    operatorName: row.operatorName || '--',
    orderableQtyBefore: reservationBefore,
    orderableQtyAfter: reservationAfter,
    commissionBefore,
    commissionAfter,
    expectedOrderTimeBefore,
    expectedOrderTimeAfter,
    remark: row.remark || '--',
    changeTime: row.changeTime || '--'
  }
}

export function formatDetailReservationQtyText(row = {}) {
  return formatReservationAmountText(row.reservationType, row.reservationQty, row.reservationGmv)
}

export function formatFeedbackOrderableText(row = {}) {
  return formatReservationAmountText(row.orderableType, row.orderableQty, row.orderableGmv)
}

export function formatFeedbackCommissionText(row = {}) {
  if (row.commissionType === COMMISSION_TYPE.RATE) {
    return `佣金比例 ${row.promotionRate != null ? row.promotionRate : '--'}%`
  }
  return `固定金额 ${row.promotionAmount != null ? row.promotionAmount : '--'}元/件`
}

export function formatListCommissionText(row = {}) {
  if (row.commissionType === COMMISSION_TYPE.RATE) {
    return `佣金比例 ${row.promotionRate != null ? row.promotionRate : '--'}%`
  }
  return `固定金额 ${row.promotionAmount != null ? row.promotionAmount : '--'}元/件`
}

export function formatListReservationText(row = {}) {
  if (row.reservationType === RESERVATION_TYPE.GMV) {
    return `下单金额 ${row.reservationGmv != null ? row.reservationGmv : '--'}元`
  }
  return `下单数量 ${row.reservationQty != null ? row.reservationQty : '--'}件`
}

export function buildBizUpdatePayload({ reservation, form, channel, confirmEditCompleted = false }) {
  const payload = {
    id: reservation.id,
    reservationType: form.reservationType,
    commissionType: form.commissionType,
    expectedOrderTime: form.expectedOrderTime,
    remark: form.remark || '',
    confirmEditCompleted
  }

  if (form.reservationType === RESERVATION_TYPE.QUANTITY) {
    payload.reservationQty = Number(form.reservationAmount)
  } else {
    payload.reservationGmv = Number(form.reservationAmount)
  }

  if (form.commissionType === COMMISSION_TYPE.RATE) {
    payload.promotionRate = Number(form.commissionValue)
  } else {
    payload.promotionAmount = Number(form.commissionValue)
  }

  payload.channelAccountId = form.channelAccountId
    || (channel && channel.accountId)
    || reservation.channelAccountId
  payload.channelName = (channel && channel.nickName) || reservation.channelName || ''

  return payload
}

export function reservationTypeLabel(type) {
  return type === RESERVATION_TYPE.GMV ? '按GMV预约' : '按数量预约'
}

export function commissionTypeLabel(type) {
  return type === COMMISSION_TYPE.FIXED ? '固定佣金' : '佣金比例'
}

export function reservationToEditForm(reservation = {}) {
  const reservationType = reservation.reservationType || RESERVATION_TYPE.QUANTITY
  const commissionType = reservation.commissionType || COMMISSION_TYPE.RATE
  return {
    reservationType,
    reservationAmount: reservationType === RESERVATION_TYPE.GMV
      ? (reservation.reservationGmv != null ? String(reservation.reservationGmv) : '')
      : (reservation.reservationQty != null ? String(reservation.reservationQty) : ''),
    commissionType,
    commissionValue: commissionType === COMMISSION_TYPE.FIXED
      ? (reservation.promotionAmount != null ? String(reservation.promotionAmount) : '')
      : (reservation.promotionRate != null ? String(reservation.promotionRate) : ''),
    expectedOrderTime: reservation.expectedOrderTime || '',
    remark: reservation.remark || '',
    channelAccountId: reservation.channelAccountId || ''
  }
}

export function formatEditChangeLogItem(row = {}) {
  return {
    operatorName: row.operatorName || '--',
    remark: row.remark || '--',
    changeTime: row.changeTime || '--'
  }
}

export function validateChannelAccountId(rule, value, callback) {
  if (value === '' || value == null) {
    callback(new Error('请选择渠道'))
    return
  }
  callback()
}

export function getChannelAccountIdRules() {
  return [
    { required: true, message: '请选择渠道', trigger: 'change' },
    { validator: validateChannelAccountId, trigger: 'change' }
  ]
}

export function getBizEditFormRules() {
  const rules = getVolumeFormRules()
  return {
    // channelAccountId: getChannelAccountIdRules(),
    reservationAmount: rules.reservationAmount,
    commissionValue: rules.commissionValue,
    expectedOrderTime: rules.expectedOrderTime
  }
}

export function buildQdSubmitPayload({ product, form, salesPathOptions = [] }) {
  const salesPath = salesPathOptions.find(item => item.value === form.salesPathCode) || {}
  const payload = {
    productName: getProductFullName(product),
    productMainImage: product.productMainImage || product.productImage || '',
    salesPathCode: form.salesPathCode,
    salesPathName: salesPath.label || '',
    reservationType: form.reservationType,
    commissionType: form.commissionType,
    expectedOrderTime: form.expectedOrderTime,
    remark: form.remark || ''
  }

  if (form.reservationType === RESERVATION_TYPE.QUANTITY) {
    payload.reservationQty = Number(form.reservationAmount)
  } else {
    payload.reservationGmv = Number(form.reservationAmount)
  }

  if (form.commissionType === COMMISSION_TYPE.RATE) {
    payload.promotionRate = Number(form.commissionValue)
  } else {
    payload.promotionAmount = Number(form.commissionValue)
  }

  return payload
}

export function buildBizCreatePayload({ product, form, salesPathOptions = [], channel = {} }) {
  return {
    ...buildQdSubmitPayload({ product, form, salesPathOptions }),
    channelAccountId: channel.accountId,
    channelName: channel.nickName || '',
    status: 2
  }
}

export function loadSalesPathOptions(vm) {
  return getSupShopTagListByTagType.call(vm, {
    pageIndex: 1,
    pageSize: 1000,
    tagType: 'SALES_PATH'
  }).then(res => {
    const records = res || []
    return records.map(item => ({
      label: item.tagLabel,
      value: item.tagCode
    }))
  }).catch(() => [])
}

export function getDefaultVolumeForm() {
  return {
    salesPathCode: '',
    reservationType: RESERVATION_TYPE.QUANTITY,
    reservationAmount: '',
    commissionType: COMMISSION_TYPE.RATE,
    commissionValue: '',
    expectedOrderTime: '',
    remark: ''
  }
}

function createPositiveNumberValidator(emptyMessage, invalidMessage = '请输入有效的数值') {
  return (rule, value, callback) => {
    if (value === '' || value == null) {
      callback(new Error(emptyMessage))
      return
    }
    const num = Number(value)
    if (Number.isNaN(num) || num <= 0) {
      callback(new Error(invalidMessage))
      return
    }
    callback()
  }
}

export function validateExpectedOrderTime(rule, value, callback) {
  if (!value) {
    callback(new Error('请选择预期下单时间'))
    return
  }
  const selected = new Date(String(value).replace(/-/g, '/'))
  if (selected.getTime() < Date.now()) {
    callback(new Error('预期下单时间不能早于当前时间'))
    return
  }
  callback()
}

export function getExpectedOrderPickerOptions(expectedOrderTime) {
  const now = new Date()
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const pad = (num) => String(num).padStart(2, '0')
  const options = {
    disabledDate(time) {
      return time.getTime() < todayStart
    }
  }

  if (expectedOrderTime) {
    const selected = new Date(String(expectedOrderTime).replace(/-/g, '/'))
    const selectedDayStart = new Date(
      selected.getFullYear(),
      selected.getMonth(),
      selected.getDate()
    ).getTime()
    if (selectedDayStart === todayStart) {
      options.selectableRange = [
        `${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())} - 23:59:59`
      ]
    } else {
      options.selectableRange = ['00:00:00 - 23:59:59']
    }
  }

  return options
}

export function getVolumeFormRules() {
  return {
    salesPathCode: [{ required: true, message: '请选择下单路径', trigger: 'change' }],
    reservationType: [{ required: true, message: '请选择所需库存', trigger: 'change' }],
    reservationAmount: [
      { required: true, message: '请输入约量数值', trigger: 'blur' },
      { validator: createPositiveNumberValidator('请输入约量数值'), trigger: 'blur' }
    ],
    commissionType: [{ required: true, message: '请选择佣金', trigger: 'change' }],
    commissionValue: [
      { required: true, message: '请输入返佣数值', trigger: 'blur' },
      { validator: createPositiveNumberValidator('请输入返佣数值'), trigger: 'blur' }
    ],
    expectedOrderTime: [
      { required: true, message: '请选择预期下单时间', trigger: 'change' },
      { validator: validateExpectedOrderTime, trigger: 'change' }
    ]
  }
}

export function validateVolumeForm(formRef, vm) {
  return new Promise(resolve => {
    if (!formRef) {
      resolve(false)
      return
    }
    formRef.validate(valid => {
      if (!valid) {
        vm.$message.warning('请完善必填信息')
        vm.$nextTick(() => {
          const firstError = vm.$el.querySelector('.el-form-item.is-error')
          if (firstError) {
            firstError.scrollIntoView({ behavior: 'smooth', block: 'center' })
          }
        })
      }
      resolve(valid)
    })
  })
}
